import { QueryKeys } from "@/constants/Query";
import { useApi } from "@/hooks/useApi";
import { useApiMutation } from "@/hooks/useApiMutation";
import axiosInstance from "@/lib/axios";
import {
  type CodeableConcept,
  FamilyMemberHistory,
} from "@curai/js-fhir-tools";
import { useQueryClient } from "@tanstack/react-query";

interface AddFamilyMemberHistoryRequest {
  patientId: string;
  relationshipCode: string;
  conditionCodes: Pick<CodeableConcept, "coding" | "text">[];
}

export const useAddFamilyMemberHistory = ({
  patientId,
  onSuccess,
  onError,
}: {
  patientId: string;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) => {
  const queryClient = useQueryClient();
  return useApiMutation({
    mutationOptions: {
      mutationFn: (data: AddFamilyMemberHistoryRequest) =>
        axiosInstance.post("/charting/family-history/", data),
      mutationKey:
        QueryKeys.Patient.MedicalRecords.FamilyMemberHistory.CREATE(patientId),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey:
            QueryKeys.Patient.MedicalRecords.FamilyMemberHistory.DEFAULT(
              patientId,
            ),
        });
        queryClient.invalidateQueries({
          queryKey:
            QueryKeys.Patient.MedicalRecords.FamilyMemberHistory.OPTIONS(
              patientId,
            ),
        });
        onSuccess?.();
      },
      onError,
    },
  });
};

interface GetFamilyMemberHistoryResponse {
  familyMemberHistory: FamilyMemberHistory[];
}

export const useGetFamilyMemberHistory = ({
  patientId,
}: { patientId: string }) => {
  return useApi<GetFamilyMemberHistoryResponse>({
    queryOptions: {
      queryKey:
        QueryKeys.Patient.MedicalRecords.FamilyMemberHistory.DEFAULT(patientId),
      queryFn: async () => {
        const response = await axiosInstance.get("/charting/family-history/", {
          params: { patientId },
        });
        const data: GetFamilyMemberHistoryResponse = response?.data;
        data.familyMemberHistory = data.familyMemberHistory.map(
          (resource: object) => new FamilyMemberHistory(resource),
        );
        return data;
      },
      enabled: Boolean(patientId),
    },
  });
};

interface UpdateFamilyMemberHistoryRequest {
  relationshipCode: string;
  conditionCodes: Pick<CodeableConcept, "coding" | "text">[];
}

export const useUpdateFamilyMemberHistory = ({
  familyMemberHistoryId,
  patientId,
  onSuccess,
}: {
  familyMemberHistoryId: string;
  patientId: string;
  onSuccess?: () => void;
}) => {
  const queryClient = useQueryClient();
  return useApiMutation({
    mutationOptions: {
      mutationFn: (data: UpdateFamilyMemberHistoryRequest) =>
        axiosInstance.patch(
          `/charting/family-history/${familyMemberHistoryId}`,
          data,
        ),
      mutationKey: QueryKeys.Patient.MedicalRecords.FamilyMemberHistory.UPDATE(
        patientId,
        familyMemberHistoryId,
      ),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey:
            QueryKeys.Patient.MedicalRecords.FamilyMemberHistory.DEFAULT(
              patientId,
            ),
        });
        onSuccess?.();
      },
    },
  });
};

export const useVoidFamilyMemberHistory = ({
  familyMemberHistoryId,
  patientId,
  onSuccess,
}: {
  familyMemberHistoryId: string;
  patientId: string;
  onSuccess?: () => void;
}) => {
  const queryClient = useQueryClient();

  return useApiMutation<void, void>({
    mutationOptions: {
      mutationFn: () =>
        axiosInstance.delete(
          `/charting/family-history/${familyMemberHistoryId}`,
        ),
      mutationKey: QueryKeys.Patient.MedicalRecords.FamilyMemberHistory.VOID(
        patientId,
        familyMemberHistoryId,
      ),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey:
            QueryKeys.Patient.MedicalRecords.FamilyMemberHistory.DEFAULT(
              patientId,
            ),
        });
        queryClient.invalidateQueries({
          queryKey:
            QueryKeys.Patient.MedicalRecords.FamilyMemberHistory.OPTIONS(
              patientId,
            ),
        });
        onSuccess?.();
      },
    },
  });
};

interface GetFamilyMemberHistoryOptionsResponse {
  relationshipOptions: {
    code: string;
    display: string;
  }[];
}

export const useGetFamilyMemberHistoryOptions = (patientId: string) => {
  return useApi<GetFamilyMemberHistoryOptionsResponse>({
    queryOptions: {
      queryKey:
        QueryKeys.Patient.MedicalRecords.FamilyMemberHistory.OPTIONS(patientId),
      queryFn: async () => {
        const response = await axiosInstance.get(
          "/charting/family-history/relationship-options",
          {
            params: { patientId },
          },
        );
        return response?.data;
      },
      enabled: Boolean(patientId),
    },
  });
};
